import { trans } from "@mongez/localization";
import { __DEV__ } from "framework/config";
import { Component, ErrorInfo, ReactNode } from "react";
import { extractComponentName, getErrorLocationInfo } from "../utils";

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
};

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, errorInfo: ErrorInfo) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
};

export class AppErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    console.log("Error in AppErrorBoundary", error, errorInfo);

    // Call onError handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        // Check if fallback is a function
        if (typeof this.props.fallback === "function") {
          // Call function with error and errorInfo if both are available
          if (this.state.error && this.state.errorInfo) {
            return this.props.fallback(this.state.error, this.state.errorInfo);
          }
          // Fallback to default if error/errorInfo not yet set
        } else {
          // Render as ReactNode
          return this.props.fallback;
        }
      }

      // Default error UI
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <div className="mb-4 flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-8 w-8 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  {trans("somethingWentWrong")}
                </h3>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                {trans("errorBoundaryMessage")}
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {trans("reloadPage")}
              </button>
              <button
                onClick={() => window.history.back()}
                className="flex-1 rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {trans("goBack")}
              </button>
            </div>

            {__DEV__ && this.state.error && this.state.errorInfo && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-gray-700">
                  {trans("errorDetails")}
                </summary>
                <div className="mt-2 rounded-md bg-gray-100 p-3">
                  <div className="mb-3">
                    <h4 className="font-semibold text-gray-800">
                      Error Location:
                    </h4>
                    <p className="text-sm text-gray-700">
                      <strong>Component:</strong>{" "}
                      {extractComponentName(
                        this.state.errorInfo.componentStack || "",
                      )}
                    </p>
                    {(() => {
                      const locationInfo = getErrorLocationInfo(
                        this.state.error!,
                        this.state.errorInfo!.componentStack || "",
                      );
                      return (
                        <>
                          {locationInfo.filePath && (
                            <p className="text-sm text-gray-700">
                              <strong>File:</strong> {locationInfo.filePath}
                            </p>
                          )}
                          {locationInfo.lineNumber && (
                            <p className="text-sm text-gray-700">
                              <strong>Line:</strong> {locationInfo.lineNumber}
                            </p>
                          )}
                          <p className="text-sm text-gray-700">
                            <strong>Error Type:</strong>{" "}
                            {locationInfo.errorType}
                          </p>
                        </>
                      );
                    })()}
                  </div>
                  <div className="mb-3">
                    <h4 className="font-semibold text-gray-800">
                      Error Message:
                    </h4>
                    <p className="text-sm text-gray-700">
                      {this.state.error.message}
                    </p>
                  </div>
                  {this.state.error.stack && (
                    <div>
                      <h4 className="font-semibold text-gray-800">
                        Stack Trace:
                      </h4>
                      <pre className="whitespace-pre-wrap text-xs text-gray-800">
                        {this.state.error.stack}
                      </pre>
                    </div>
                  )}
                  {this.state.errorInfo.componentStack && (
                    <div className="mt-3">
                      <h4 className="font-semibold text-gray-800">
                        Component Stack:
                      </h4>
                      <pre className="whitespace-pre-wrap text-xs text-gray-800">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
